import { Routes } from "@angular/router";

import {
	ConfirmComponent,
	LogOutComponent,
	MFAComponent,
	MfaSetupComponent,
	MustSetMainFunctionProblemComponent,
	NoPermissionsComponent,
	PermissionsGuard,
	PrivacyGuard,
	SelectOrganisationComponent,
	SelectOrganisationGuard,
	SignInComponent,
	SignInWithUsernameComponent,
	UnknownProblemComponent,
} from "@intocare/auth";
import { RouteNotFoundComponent } from "@intocare/core";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { portalGlobalOverlayRoutes } from "@intocare/features/observations/portal-global-overlays";

import { HomeComponent } from "./core/containers/home/home.component";
import {
	AuthenticatedGuard,
	ConfirmPasswordGuard,
	IsAuthenticatedGuard,
	NotAuthenticatedGuard,
	OrganisationGuard,
} from "./core/guards";
import * as permissions from "./core/permissions/permissions";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ErrorPageComponent } from "../../../../libs/core/src/containers/error-page/error-page.component";

export const routes: Routes = [
	{
		path: "select-organisation",
		component: SelectOrganisationComponent,
		canActivate: [SelectOrganisationGuard],
	},
	{
		path: ":org/:home",
		canActivate: [OrganisationGuard],
		children: [
			{
				path: "no-permissions",
				component: NoPermissionsComponent,
				canActivate: [IsAuthenticatedGuard],
			},
			{
				path: "sign-in",
				component: SignInComponent,
				canActivate: [OrganisationGuard, NotAuthenticatedGuard],
			},
			{
				path: "log-out",
				component: LogOutComponent,
			},
			{
				path: "confirm",
				component: ConfirmComponent,
				canActivate: [OrganisationGuard, ConfirmPasswordGuard],
			},
			{
				path: "mfa",
				component: MFAComponent,
				canActivate: [OrganisationGuard, NotAuthenticatedGuard],
			},
			{
				path: "sign-in-with-username",
				component: SignInWithUsernameComponent,
				canActivate: [OrganisationGuard, NotAuthenticatedGuard],
			},
			{
				path: "mfa-setup",
				component: MfaSetupComponent,
				canActivate: [OrganisationGuard, AuthenticatedGuard],
			},
			{
				path: "users",
				component: HomeComponent,
				canActivate: [OrganisationGuard, AuthenticatedGuard, PrivacyGuard, PermissionsGuard],
				data: {
					permissions: ["PORTAL.INTERNAL_EMPLOYEES"],
					redirectTo: "../organisation",
				},
				children: [
					{
						path: "",
						// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
						loadChildren: async () => (await import("./+users/users.module")).UsersModule,
					},
				],
			},
			{
				path: "",
				component: HomeComponent,
				canActivate: [OrganisationGuard, AuthenticatedGuard],
				children: [
					{
						path: "",
						canActivate: [PrivacyGuard],
						children: [
							{ path: "", redirectTo: "dashboard", pathMatch: "full" },
							{
								path: "dashboard",
								// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
								loadChildren: async () => (await import("./+dashboard/dashboard.module")).DashboardModule,
								canActivate: [OrganisationGuard, PermissionsGuard],
								data: {
									redirectTo: "../users",
								},
							},
							{
								path: "communication",
								// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
								loadChildren: () => import("@intocare/features/internal-communication/portal"),
								canActivate: [PermissionsGuard],
								data: {
									permissions: ["PORTAL.INTERNAL_COMMUNICATION"],
									redirectTo: "../dashboard",
								},
							},
							{
								path: "organisation",
								// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
								loadChildren: async () => (await import("./+organisation/organisation.module")).OrganisationModule,
								canActivate: [OrganisationGuard, PermissionsGuard],
								data: {
									permissions: permissions.ORGANISATION,
									redirectTo: "../residents/management/onboarding",
								},
							},
							{
								path: "residents",
								// See https://github.com/angular/angular/issues/46697 and https://github.com/microsoft/TypeScript/issues/47682
								loadChildren: async () => (await import("./+residents/residents.module")).ResidentsModule,
								canActivate: [OrganisationGuard, PermissionsGuard],
								data: {
									permissions: permissions.RESIDENTS,
									redirectTo: "../dashboard",
								},
							},
							{ path: "**", component: RouteNotFoundComponent },
						],
					},
					...portalGlobalOverlayRoutes,
				],
			},
		],
	},
	{
		path: "",
		redirectTo: "select-organisation",
		pathMatch: "full",
	},
	{
		path: "error",
		pathMatch: "full",
		component: ErrorPageComponent,
	},
	{
		path: "no-permission",
		pathMatch: "full",
		component: NoPermissionsComponent,
	},
	{
		path: "unknown-problem",
		pathMatch: "full",
		component: UnknownProblemComponent,
	},
	{
		path: "must-set-main-function",
		pathMatch: "full",
		component: MustSetMainFunctionProblemComponent,
	},
];
